import React from "react";
import { Link } from "react-router-dom";
import { useTheme } from "@mui/styles";
import { Typography } from "@mui/material";
import dayjs from "dayjs";
import Tooltiper from "../../../../components/Tooltiper";
import EquipmentRowName from "../../../../components/EquipmentRowName";
import { TagStatusRepairRequest } from "./TagStatusRepairRequest";
import HistoryRepairRequest from "./HistoryRepairRequest";
import ActionsRepairRequest from "./ActionsRepairRequest";
import {
    EquipmentRepairRequestEntity,
    EquipmentTypeDictionaryEntity,
    SportKindDictionaryEntity,
    SportObjectEntity,
    TechnicalCondition,
    Maybe
} from "../../../../graphql/graphQlApiHooks";

interface EquipmentCellProps {
    row: EquipmentRepairRequestEntity;
}

export const EquipmentCell = React.memo(function EquipmentCell({ row }: EquipmentCellProps) {
    const {
        sportEquipment: {
            name,
            inUse,
            inventoryNumber,
            technicalCondition,
            failureReason,
            photos,
            mainPhoto,
            id,
            isEditable
        }
    } = row;

    const avatarUrl = mainPhoto ? mainPhoto.avatarUrl : (photos && photos?.length > 0) ? photos?.[0].avatarUrl : undefined;
    const avatarUrlOrigin = mainPhoto ? mainPhoto.url : (photos && photos?.length > 0) ? photos?.[0].url : undefined;

    return (
        <Link to={`/repairs-request/equipments/${id}`}>
            <EquipmentRowName
                name={name}
                inUse={inUse}
                inventoryNumber={inventoryNumber}
                technicalCondition={technicalCondition === TechnicalCondition.Working}
                failureReason={failureReason}
                avatarUrl={avatarUrl}
                avatarUrlOrigin={avatarUrlOrigin}
                isEditable={isEditable}
            />
        </Link>
    );
}, (prevProps, nextProps) => {
    const prev = prevProps.row.sportEquipment;
    const next = nextProps.row.sportEquipment;
    
    return (
        prev.id === next.id &&
        prev.name === next.name &&
        prev.inventoryNumber === next.inventoryNumber &&
        prev.technicalCondition === next.technicalCondition &&
        prev.inUse === next.inUse &&
        prev.failureReason === next.failureReason &&
        prev.isEditable === next.isEditable &&
        prev.mainPhoto?.id === next.mainPhoto?.id &&
        prev.mainPhoto?.avatarUrl === next.mainPhoto?.avatarUrl &&
        prev.mainPhoto?.url === next.mainPhoto?.url &&
        prev.photos?.length === next.photos?.length &&
        (prev.photos?.length === 0 || prev.photos?.[0]?.id === next.photos?.[0]?.id)
    );
});

interface SportObjectCellProps {
    sportObject: SportObjectEntity;
    inUse: boolean;
}

export const SportObjectCell = React.memo(function SportObjectCell({ sportObject, inUse }: SportObjectCellProps) {
    const theme = useTheme();
    return (
        <Tooltiper
            text={sportObject?.name}
            props={{
                variant: !sportObject?.name ? "body2" : "subtitle1",
                style: {
                    ...(!inUse && { color: theme.palette.grey["500"] })
                }
            }}
        />
    );
}, (prevProps, nextProps) => {
    return (
        prevProps.sportObject?.id === nextProps.sportObject?.id &&
        prevProps.sportObject?.name === nextProps.sportObject?.name &&
        prevProps.inUse === nextProps.inUse
    );
});

interface EquipmentTypeCellProps {
    equipmentTypeObject: Maybe<EquipmentTypeDictionaryEntity>;
    inUse: boolean;
}

export const EquipmentTypeCell = React.memo(function EquipmentTypeCell({ equipmentTypeObject, inUse }: EquipmentTypeCellProps) {
    const theme = useTheme();
    return (
        <Tooltiper
            text={equipmentTypeObject?.name}
            props={{
                variant: !equipmentTypeObject?.name ? "body2" : "subtitle1",
                style: {
                    ...(!inUse && { color: theme.palette.grey["500"] })
                }
            }}
        />
    );
}, (prevProps, nextProps) => {
    return (
        prevProps.equipmentTypeObject?.id === nextProps.equipmentTypeObject?.id &&
        prevProps.equipmentTypeObject?.name === nextProps.equipmentTypeObject?.name &&
        prevProps.inUse === nextProps.inUse
    );
});

interface SportKindCellProps {
    sportKindObject: Maybe<SportKindDictionaryEntity>;
    inUse: boolean;
}

export const SportKindCell = React.memo(function SportKindCell({ sportKindObject, inUse }: SportKindCellProps) {
    const theme = useTheme();
    return (
        <Tooltiper
            text={sportKindObject?.name}
            props={{
                variant: !sportKindObject?.name ? "body2" : "subtitle1",
                style: {
                    ...(!inUse && { color: theme.palette.grey["500"] })
                }
            }}
        />
    );
}, (prevProps, nextProps) => {
    return (
        prevProps.sportKindObject?.id === nextProps.sportKindObject?.id &&
        prevProps.sportKindObject?.name === nextProps.sportKindObject?.name &&
        prevProps.inUse === nextProps.inUse
    );
});

interface StatusCellProps {
    row: EquipmentRepairRequestEntity;
}

export const StatusCell = React.memo(function StatusCell({ row }: StatusCellProps) {
    return (
        <TagStatusRepairRequest stateHistory={row.stateHistory} />
    );
}, (prevProps, nextProps) => {
    const prevLastState = prevProps.row.stateHistory?.[prevProps.row.stateHistory.length - 1];
    const nextLastState = nextProps.row.stateHistory?.[nextProps.row.stateHistory.length - 1];

    return (
        prevProps.row.id === nextProps.row.id &&
        prevProps.row.stateHistory.length === nextProps.row.stateHistory.length &&
        prevLastState?.status === nextLastState?.status
    );
});

interface HistoryCellProps {
    row: EquipmentRepairRequestEntity;
}

export const HistoryCell = React.memo(function HistoryCell({ row }: HistoryCellProps) {
    return (
        <HistoryRepairRequest
            stateHistory={row.stateHistory}
            equipment={row.sportEquipment}
        />
    );
}, (prevProps, nextProps) => {
    const prevLastState = prevProps.row.stateHistory?.[prevProps.row.stateHistory.length - 1];
    const nextLastState = nextProps.row.stateHistory?.[nextProps.row.stateHistory.length - 1];

    return (
        prevProps.row.id === nextProps.row.id &&
        prevProps.row.stateHistory.length === nextProps.row.stateHistory.length &&
        prevLastState?.status === nextLastState?.status
    );
});

interface ActionsCellProps {
    row: EquipmentRepairRequestEntity;
}

export const ActionsCell = React.memo(function ActionsCell({ row }: ActionsCellProps) {
    return (
        <ActionsRepairRequest
            equipmentRepairRequest={row}
        />
    );
}, (prevProps, nextProps) => {
    const prevLastState = prevProps.row.stateHistory?.[prevProps.row.stateHistory.length - 1];
    const nextLastState = nextProps.row.stateHistory?.[nextProps.row.stateHistory.length - 1];

    return (
        prevProps.row.id === nextProps.row.id &&
        prevProps.row.stateHistory.length === nextProps.row.stateHistory.length &&
        prevLastState?.status === nextLastState?.status
    );
});

interface DateCellProps {
    createdAt: string;
}

export const DateCell = React.memo(function DateCell({ createdAt }: DateCellProps) {
    return (
        <Typography variant="body2">
            {createdAt ? dayjs(createdAt).format('DD.MM.YYYY HH:mm') : '-'}
        </Typography>
    );
}, (prevProps, nextProps) => {
    return prevProps.createdAt === nextProps.createdAt;
});

